import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Globe, Menu, X, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sectorsOpen, setSectorsOpen] = useState(false);
  const location = useLocation();
  const isHome = location.pathname === '/' || location.pathname === '/home';

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    onScroll();
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setSectorsOpen(false);
    window.scrollTo(0, 0);
  }, [location.pathname]);

  const solid = scrolled || !isHome;
  const isActive = (p) => location.pathname === p;

  const navItems = [
    { label: 'Home', to: '/' },
    { label: 'Community', to: '/community' },
    { label: 'Contact', to: '/contact' },
  ];

  return (
    <header
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        solid ? 'bg-[#071627]/95 backdrop-blur-md py-3 shadow-lg shadow-black/20' : 'bg-transparent py-6'
      }`}
    >
      <div className="container-lg flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group">
          <Globe className="text-[#c6a15b] transition-transform duration-500 group-hover:rotate-12" size={34} strokeWidth={1.2} />
          <span className="h-8 w-px bg-[#c6a15b]/60" />
          <span className="flex flex-col leading-none">
            <span className="font-serif text-[#f7f4ee] text-2xl tracking-[0.14em] font-semibold">LRG HOLDINGS</span>
            <span className="text-[#c6a15b] text-[0.55rem] tracking-[0.32em] uppercase mt-1">Bringing Botswana to the World</span>
          </span>
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-10">
          <Link to="/" className={`nav-link text-[#f7f4ee] text-[0.78rem] tracking-[0.22em] uppercase font-medium ${isActive('/') ? 'active' : ''}`}>Home</Link>
          <div className="relative" onMouseEnter={() => setSectorsOpen(true)} onMouseLeave={() => setSectorsOpen(false)}>
            <button className={`nav-link flex items-center gap-1.5 text-[#f7f4ee] text-[0.78rem] tracking-[0.22em] uppercase font-medium ${(isActive('/diamonds') || isActive('/education') || isActive('/leather')) ? 'active' : ''}`}>
              Sectors <ChevronDown size={14} className={`transition-transform duration-300 ${sectorsOpen ? 'rotate-180' : ''}`} />
            </button>
            <div className={`absolute top-full left-0 pt-4 transition-all duration-300 ${sectorsOpen ? 'opacity-100 visible translate-y-0' : 'opacity-0 invisible -translate-y-2'}`}>
              <div className="bg-[#071627] border border-[#c6a15b]/25 min-w-[200px] py-2">
                <Link to="/education" className="block px-6 py-3 text-[#f7f4ee] text-[0.75rem] tracking-[0.18em] uppercase hover:bg-[#12314f] hover:text-[#c6a15b] transition-colors">Education</Link>
                <Link to="/diamonds" className="block px-6 py-3 text-[#f7f4ee] text-[0.75rem] tracking-[0.18em] uppercase hover:bg-[#12314f] hover:text-[#c6a15b] transition-colors">Diamonds</Link>
                <Link to="/leather" data-testid="nav-leather-link" className="block px-6 py-3 text-[#f7f4ee] text-[0.75rem] tracking-[0.18em] uppercase hover:bg-[#12314f] hover:text-[#c6a15b] transition-colors">Leather</Link>
              </div>
            </div>
          </div>
          <Link to="/community" className={`nav-link text-[#f7f4ee] text-[0.78rem] tracking-[0.22em] uppercase font-medium ${isActive('/community') ? 'active' : ''}`}>Community</Link>
          <Link to="/contact" className={`nav-link text-[#f7f4ee] text-[0.78rem] tracking-[0.22em] uppercase font-medium ${isActive('/contact') ? 'active' : ''}`}>Contact</Link>
        </nav>

        <button className="lg:hidden text-[#f7f4ee]" onClick={() => setMobileOpen(true)} aria-label="Open menu">
          <Menu size={28} strokeWidth={1.4} />
        </button>
      </div>

      {/* Mobile drawer */}
      <div className={`fixed inset-0 z-50 lg:hidden overflow-hidden transition-all duration-400 ${mobileOpen ? 'visible' : 'invisible'}`}>
        <div className={`absolute inset-0 bg-black/60 transition-opacity duration-400 ${mobileOpen ? 'opacity-100' : 'opacity-0'}`} onClick={() => setMobileOpen(false)} />
        <div className={`absolute top-0 right-0 h-full w-[80%] max-w-sm bg-[#071627] p-8 transition-transform duration-500 ${mobileOpen ? 'translate-x-0' : 'translate-x-full'}`}>
          <div className="flex justify-end mb-10">
            <button onClick={() => setMobileOpen(false)} className="text-[#f7f4ee]" aria-label="Close menu"><X size={28} /></button>
          </div>
          <nav className="flex flex-col gap-6">
            <Link to="/" className="text-[#f7f4ee] text-lg tracking-[0.18em] uppercase">Home</Link>
            <div>
              <p className="eyebrow-light mb-3" style={{color:'#c6a15b'}}>Sectors</p>
              <div className="flex flex-col gap-4 pl-4 border-l border-[#c6a15b]/30">
                <Link to="/education" className="text-[#f7f4ee]/85 tracking-[0.14em] uppercase text-sm">Education</Link>
                <Link to="/diamonds" className="text-[#f7f4ee]/85 tracking-[0.14em] uppercase text-sm">Diamonds</Link>
                <Link to="/leather" className="text-[#f7f4ee]/85 tracking-[0.14em] uppercase text-sm">Leather</Link>
              </div>
            </div>
            <Link to="/community" className="text-[#f7f4ee] text-lg tracking-[0.18em] uppercase">Community</Link>
            <Link to="/contact" className="text-[#f7f4ee] text-lg tracking-[0.18em] uppercase">Contact</Link>
          </nav>
        </div>
      </div>
    </header>
  );
};

export default Navbar;
